import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTabStore } from '../store/tabStore'
import { useLanguageStore } from '../store/languageStore'
import {
  buildSettingsUrl,
  getItemById,
  SCREEN_CONTEXT_DOMAIN,
  type SettingsDomainId,
  type SettingsScreenContext,
} from '../data/settingsRegistry'
import { useSettingsFavoritesStore } from '../store/settingsFavoritesStore'

export interface OpenSettingsOptions {
  domain?: SettingsDomainId
  context?: SettingsScreenContext
  itemId?: string
  query?: string
  /** Opens Settings Center focused on a named page (Page.{pageId}) */
  pageId?: string
}

/**
 * Navigate to the central Settings Control Center, optionally deep-linking
 * to a domain, item, or screen context.
 */
export function useOpenSettings() {
  const navigate = useNavigate()
  const { addTab } = useTabStore()
  const { language } = useLanguageStore()
  const trackRecent = useSettingsFavoritesStore((s) => s.trackRecent)
  const isRTL = language === 'ar'

  const openSettings = useCallback(
    (options: OpenSettingsOptions = {}) => {
      const domain =
        options.domain ||
        (options.context ? SCREEN_CONTEXT_DOMAIN[options.context] : undefined) ||
        (options.itemId ? getItemById(options.itemId)?.domain : undefined)

      if (options.itemId) trackRecent(options.itemId)

      const url = buildSettingsUrl({
        domain,
        query: options.query,
        itemId: options.itemId,
        context: options.context,
        pageId: options.pageId,
      })

      const title = isRTL ? 'مركز الإعدادات' : 'Settings Center'
      const opened = addTab({
        id: 'settings',
        title,
        basePath: '/settings',
        lastPath: url,
      })
      if (opened) navigate(url)
    },
    [addTab, navigate, isRTL, trackRecent]
  )

  const openDomain = useCallback(
    (domain: SettingsDomainId, context?: SettingsScreenContext) =>
      openSettings({ domain, context }),
    [openSettings]
  )

  const openItem = useCallback(
    (itemId: string) => openSettings({ itemId }),
    [openSettings]
  )

  const openPage = useCallback(
    (pageId: string, domain?: SettingsDomainId, context?: SettingsScreenContext) =>
      openSettings({ pageId, domain, context }),
    [openSettings]
  )

  return { openSettings, openDomain, openItem, openPage }
}
